import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Dialog,
  Field,
  HStack,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CopyableRow } from './CopyableRow';
import {
  Button as ButtonComponent,
  InputField,
  Tabs,
  Textarea,
  type Tab,
} from '@themes/aegix';
import { useMutation } from '@tanstack/react-query';
import { toaster } from '../../ui/toaster';
import {
  uploadGoogleScimCreds,
  type UploadGoogleCredsResponse,
} from '../../../services';

/**
 * Import Google service-account credentials.
 *
 * Mirrors the SAML metadata import pattern (`ImportMetadataDialog`):
 * three tabs — Upload, Paste, Entry — converge on a service-account
 * JSON object.
 *
 *   - **Upload**: pick the `.json` file Google generated, send as-is.
 *   - **Paste**: paste the JSON contents, send as a synthesised file.
 *   - **Entry**: type just the minimum fields (`client_email`,
 *     `private_key`); the dialog wraps them in a service-account JSON.
 *
 * `admin_email` lives on the parent drawer (Tiaki-specific field —
 * NOT part of Google's service-account JSON itself, even though Google
 * needs it for domain-wide-delegation impersonation).
 *
 * Two call-site modes:
 *
 *   - **Live mode** (`configId` non-null): Apply POSTs to
 *     `/scim-configs/<id>/upload-google-creds/` immediately, then
 *     surfaces the Google Client ID for the Domain-wide-delegation
 *     paste-into-Google-Admin step.
 *   - **Staging mode** (`configId` is null, e.g. drawer's create flow):
 *     Apply returns the synthesised File to the parent via `onStaged`,
 *     which holds it in form state until Save creates the row and
 *     uploads.
 */

export interface GoogleCredsImportDialogProps {
  isOpen: boolean;
  configId: number | null;
  hasCredentials: boolean;
  adminEmail: string;
  onClose: () => void;
  /** Live mode: notify the parent that an upload landed. */
  onUploaded?: () => void;
  /** Staging mode: hand back the synthesised file for later upload. */
  onStaged?: (file: File) => void;
}

type TabId = 'upload' | 'paste' | 'entry';

const TABS: Tab[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'paste', label: 'Paste' },
  { id: 'entry', label: 'Entry' },
];

function buildSyntheticFile(json: object): File {
  return new File(
    [JSON.stringify(json, null, 2)],
    'service-account.json',
    { type: 'application/json' },
  );
}

export function GoogleCredsImportDialog({
  isOpen,
  configId,
  hasCredentials,
  adminEmail,
  onClose,
  onUploaded,
  onStaged,
}: GoogleCredsImportDialogProps) {
  const isStaging = configId === null;

  const [activeTab, setActiveTab] = useState<TabId>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [pastedJson, setPastedJson] = useState<string>('');
  const [entryClientEmail, setEntryClientEmail] = useState('');
  const [entryPrivateKey, setEntryPrivateKey] = useState('');
  const [result, setResult] = useState<UploadGoogleCredsResponse | null>(null);

  // Reset on each open so a re-open doesn't show stale state.
  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('upload');
    setFile(null);
    setPastedJson('');
    setEntryClientEmail('');
    setEntryPrivateKey('');
    setResult(null);
  }, [isOpen]);

  const buildFile = (): File => {
    if (activeTab === 'upload') {
      if (!file) throw new Error('Pick the service account JSON file first.');
      return file;
    }
    if (activeTab === 'paste') {
      if (!pastedJson.trim()) throw new Error('Paste the JSON contents first.');
      try {
        const parsed = JSON.parse(pastedJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return buildSyntheticFile(parsed as object);
        }
      } catch (err) {
        throw new Error(`Invalid JSON: ${(err as Error).message}`);
      }
      throw new Error('Pasted value must be a JSON object.');
    }
    if (!entryClientEmail.trim() || !entryPrivateKey.trim()) {
      throw new Error('Provide both client email and private key.');
    }
    return buildSyntheticFile({
      type: 'service_account',
      client_email: entryClientEmail.trim(),
      private_key: entryPrivateKey,
    });
  };

  const upload = useMutation({
    mutationFn: () => {
      if (!adminEmail.includes('@'))
        throw new Error('Set Admin Email in the drawer first.');
      const synthesised = buildFile();
      if (isStaging) {
        // Hand off to parent for save-time upload; resolve with no result.
        onStaged?.(synthesised);
        return Promise.resolve(null);
      }
      return uploadGoogleScimCreds(configId as number, synthesised, adminEmail);
    },
    onSuccess: (data) => {
      if (data) {
        setResult(data);
        onUploaded?.();
        toaster.create({
          title: 'Google credentials saved',
          description: data.detail,
          type: 'success',
          duration: 5000,
        });
      }
    },
    onError: (err) => {
      toaster.create({
        title: 'Import failed',
        description: (err as Error).message,
        type: 'error',
        duration: 8000,
      });
    },
  });

  const tabReady =
    activeTab === 'upload'
      ? !!file
      : activeTab === 'paste'
        ? !!pastedJson.trim()
        : !!entryClientEmail.trim() && !!entryPrivateKey.trim();

  const canImport =
    tabReady && adminEmail.includes('@') && !upload.isPending && !result;

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="700px"
            data-e2e="sso-google-import-dialog"
          >
            <Dialog.Header>
              <Dialog.Title>Import Google service account</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                {!adminEmail.includes('@') ? (
                  <Text fontSize="sm" color="surface.danger">
                    Set <strong>Admin Email</strong> in the drawer first;
                    Google needs it for domain-wide-delegation impersonation.
                  </Text>
                ) : (
                  <Text fontSize="sm" color="text.secondary">
                    {hasCredentials
                      ? 'Google credentials configured. Importing replaces them.'
                      : 'Upload, paste, or type the Google service-account JSON.'}
                  </Text>
                )}

                <Tabs
                  tabs={TABS}
                  activeTab={activeTab}
                  onChange={(id) => setActiveTab(id as TabId)}
                />

                {activeTab === 'upload' && (
                  <Field.Root>
                    <Field.Label htmlFor="google-creds-file">
                      Service account JSON
                    </Field.Label>
                    <HStack gap={3}>
                      <ButtonComponent
                        type="button"
                        visual="secondary"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {file ? 'Change file' : 'Choose file'}
                      </ButtonComponent>
                      <Text
                        fontSize="sm"
                        color={file ? 'text.primary' : 'text.secondary'}
                        wordBreak="break-all"
                      >
                        {file?.name ?? 'No file selected'}
                      </Text>
                    </HStack>
                    <input
                      ref={fileInputRef}
                      id="google-creds-file"
                      type="file"
                      accept=".json,application/json"
                      hidden
                      data-e2e="sso-google-creds-file"
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    />
                  </Field.Root>
                )}

                {activeTab === 'paste' && (
                  <Field.Root>
                    <Field.Label htmlFor="google-creds-paste">
                      Service account JSON
                    </Field.Label>
                    <Textarea
                      id="google-creds-paste"
                      data-e2e="sso-google-creds-paste"
                      fontFamily="mono"
                      fontSize="xs"
                      rows={10}
                      placeholder='{ "type": "service_account", "client_email": "...", "private_key": "..." }'
                      value={pastedJson}
                      onChange={(e) => setPastedJson(e.target.value)}
                    />
                  </Field.Root>
                )}

                {activeTab === 'entry' && (
                  <VStack align="stretch" gap={3}>
                    <Field.Root>
                      <Field.Label htmlFor="google-creds-client-email">
                        Service account client email
                      </Field.Label>
                      <InputField
                        id="google-creds-client-email"
                        data-e2e="sso-google-creds-client-email"
                        value={entryClientEmail}
                        onChange={(e) => setEntryClientEmail(e.target.value)}
                        placeholder="tiaki-scim@your-project.iam.gserviceaccount.com"
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label htmlFor="google-creds-private-key">
                        Private key
                      </Field.Label>
                      <Textarea
                        id="google-creds-private-key"
                        data-e2e="sso-google-creds-private-key"
                        fontFamily="mono"
                        fontSize="xs"
                        rows={8}
                        placeholder={
                          '-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----\n'
                        }
                        value={entryPrivateKey}
                        onChange={(e) => setEntryPrivateKey(e.target.value)}
                      />
                    </Field.Root>
                  </VStack>
                )}

                {result ? (
                  <Alert.Root
                    status="success"
                    data-e2e="sso-google-creds-result"
                  >
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>Credentials saved</Alert.Title>
                      <Alert.Description>
                        <VStack align="stretch" gap={3} fontSize="sm" mt={2}>
                          <CopyableRow
                            label="Client ID (Unique ID)"
                            value={result.client_id || ''}
                            fieldId="google-client-id"
                          />
                          <Text fontSize="xs" color="text.secondary">
                            Paste this Client ID into Google Admin →{' '}
                            <strong>
                              Security → API controls → Domain-wide delegation
                            </strong>{' '}
                            with the OAuth scopes listed in the Google
                            Workspace setup runbook.
                          </Text>
                        </VStack>
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                ) : null}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <ButtonComponent visual="secondary" onClick={onClose}>
                  {result ? 'Done' : 'Cancel'}
                </ButtonComponent>
                {result ? null : (
                  <ButtonComponent
                    visual="primary"
                    onClick={() => upload.mutate()}
                    disabled={!canImport}
                    data-e2e="sso-google-creds-import"
                  >
                    {upload.isPending
                      ? 'Importing…'
                      : isStaging
                        ? 'Stage'
                        : 'Import'}
                  </ButtonComponent>
                )}
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default GoogleCredsImportDialog;
